// ═══ A NEW APPEAL LEVEL TAKES THE LEVEL ABOVE'S COLUMNS (Philip, 2026-10-10) ═══
// Opening Appeal One (Two, …) used to create ONE 'County' column whatever the level above held, so a Florida
// two-base year (County + School) got a one-column appeal and the second base had to be re-added by hand — and
// under a different name was enough to break the column-name rate lookup. The new level now gets the SAME columns
// as the level above, in the same order, with the same primary column. VALUES STAY BLANK: they go in when the
// decision comes.
//
// "Copy values from <level above>" (copyLevelRows / copyDeductionLines) is for a withdrawn or no-change outcome:
// every column's values — the assessment cap, exemptions, ratio override and the deduction lines included — but
// NEVER the savings-baseline marker (it lives on Proposed only; the database refuses it elsewhere) and never a
// derived / seeded flag (a copied figure is the TS's own entry from then on, stored by Save All like any edit).

// Every per-column figure a level row carries (the fields saveAppealLevel writes, less identity and the marker).
export const LEVEL_VALUE_FIELDS = [
  'market_land', 'market_imp', 'market_pp', 'market_total',
  'cap_deduction', 'less_exemption_type', 'less_exemption_amount',
  'taxable_land', 'taxable_imp', 'taxable_pp', 'taxable_subtotal',
  'less_exemptions_type', 'less_exemptions_amount', 'taxable_total',
  'assessment_ratio', 'assessment_ratio_override', 'assessment_ratio_comment',
];

// One empty column row. `base` = { parcel_id, tax_year, appeal_level }.
export function blankLevelRow(base, jurisdiction, isPrimary) {
  const row = { ...base, jurisdiction, is_primary: isPrimary };
  LEVEL_VALUE_FIELDS.forEach((f) => { row[f] = null; });
  row.assessment_ratio_override = false;
  return row;
}

// The new level's columns: the level above's names, order and primary flag, every value blank. With nothing above
// (no Proposed yet), one primary 'County' column — what opening a level always gave.
export function levelColumnsLike(aboveRows, base) {
  const above = Object.values(aboveRows || {}).filter(Boolean);
  if (!above.length) return { County: blankLevelRow(base, 'County', true) };
  return Object.fromEntries(above.map((r) => [r.jurisdiction, blankLevelRow(base, r.jurisdiction, r.is_primary ?? null)]));
}

// The level above's columns WITH their values, re-keyed to the new level.
export function copyLevelRows(aboveRows, base) {
  return Object.fromEntries(Object.values(aboveRows || {}).filter(Boolean).map((r) => {
    const row = blankLevelRow(base, r.jurisdiction, r.is_primary ?? null);
    LEVEL_VALUE_FIELDS.forEach((f) => { row[f] = r[f] ?? row[f]; });
    return [r.jurisdiction, row];
  }));
}

// Deduction lines ({ key, type, amounts: { [jurisdiction]: amount } }) with fresh keys from nextKey().
export const copyDeductionLines = (lines, nextKey) =>
  (lines || []).map((l) => ({ key: nextKey(), type: l.type || '', amounts: { ...(l.amounts || {}) } }));

// A level is empty when no column holds a figure or a typed exemption, and it has no deduction line with an amount.
const filled = (v) => v != null && String(v).trim() !== '';
export function levelIsEmpty(rows, dedLines) {
  const anyValue = Object.values(rows || {}).some((r) => r && LEVEL_VALUE_FIELDS
    .filter((f) => f !== 'assessment_ratio_override').some((f) => filled(r[f])));
  const anyDed = (dedLines || []).some((l) => filled(l.type) || Object.values(l.amounts || {}).some(filled));
  return !anyValue && !anyDed;
}
